/**
 * @file    useEngineInit.ts
 * @scope   ENTERPRISE
 * @summary Rehydrates the matching engine subtype provider (Enterprise / CPR) from a saved config.
 * @consumes EnterpriseContext, CprContext, engine widget loader (envelope dispatch by chartTypeId)
 * @produces Nothing (imperative context writes only)
 * @sideEffects Enterprise: `setPartitions` / `setEnterpriseFields`; CPR: `RESET` with the slice.
 * @notes Mount-only effect (empty dependency array) — exactly as the original inline effect was.
 */
import { useEffect } from 'react';
import { loadEngineWidget } from 'components/DiyWidgetEngine/ChartTypes/engineChartTypeRegistry';
import { useEnterpriseContext } from 'components/DiyWidgetEngine/Subtypes/Enterprise/State/EnterpriseContext';
import { useFeatureFlagResolver } from 'components/DiyWidgetEngine/engine/featureFlags/useFeatureFlagResolver';
import { EnterpriseGlobalModel } from 'models/Enterprise/EnterpriseGlobalModel';
import { useCprContext } from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/state/CprContext';
import { CPR_CHART_TYPE } from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/constants';
import { CprSlice } from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/model';

// ⚡ ENTERPRISE V2 — NEW WORLD. Do not use for legacy charts.

/**
 * Restores the engine selection stored inside a saved widget into the matching subtype provider
 * (Enterprise: partitions + fields; CPR: whole slice).
 *
 * @param isDiyWidgetEngine Whether the currently selected chart belongs to the Enterprise world.
 * @param customWidgetConfig The persisted widget configuration to read the selection from.
 */
export function useEngineInit(isDiyWidgetEngine: boolean, customWidgetConfig: any): void {
  const { state, setPartitions, setEnterpriseFields } = useEnterpriseContext();
  const { dispatch: cprDispatch } = useCprContext();
  const isFlagEnabled = useFeatureFlagResolver();

  useEffect(() => {
    if (!isDiyWidgetEngine) return;
    const loaded = loadEngineWidget(customWidgetConfig, isFlagEnabled);
    // WHY: unknown subtype (Null Object) → leave the context empty instead of guessing a shape.
    if (loaded.status !== 'loaded') return;

    if (loaded.chartTypeId === CPR_CHART_TYPE) {
      cprDispatch({ type: 'RESET', state: loaded.slice as CprSlice });
      return;
    }

    // WHY: only rehydrate when the context is still empty, otherwise a user selection made before
    // this effect runs would be silently overwritten by the persisted one.
    if (state.selectedValues.selectedPartitions.length > 0) return;
    const { selectedPartitions, selectedEnterpriseFields } = (loaded.slice as EnterpriseGlobalModel)
      .selectedValues;
    if (selectedPartitions.length > 0) {
      setPartitions(selectedPartitions);
      setEnterpriseFields(selectedEnterpriseFields);
    }
  }, []);
}

export default useEngineInit;
